/**
 * BackgroundSubmit — optimistic-submit helper.
 *
 * Usage:
 *   1. Mount <BackgroundSubmitProvider> once (in main.tsx, outside the router).
 *   2. Call `useFireAndForget()` wherever you need to fire a callable optimistically.
 *
 * Pattern:
 *   const fireAndForget = useFireAndForget();
 *   // Show success immediately, then:
 *   fireAndForget(
 *     () => callSomeThing(payload),
 *     { label: 'Saving your review', onRetry: () => openTheFormAgainWithState() }
 *   );
 *
 * Options:
 *   - onRetry      called by the Retry button of the failure toast.
 *   - label        shown on the pending card ("Saving…" by default).
 *   - successLabel shown on the card once a visibly pending job succeeds ("Saved" by default).
 *   - confirm      show the successLabel card on success even when the job was quick (for a
 *                  message the user should always see, e.g. "HR has been notified").
 *
 * Pending / saved card: a job that is still running after ~1.2 s shows a compact card in the
 * bottom-right stack (spinner + label; "N items saving" when several run). When it succeeds the
 * card turns into a check + "Saved" for ~2.5 s and disappears. A job that finishes under the
 * threshold shows nothing (no flicker). While any job is in flight a `beforeunload` guard warns
 * before the tab is closed or reloaded.
 *
 * On failure a bottom-right toast appears with the server error message and a "Retry" button
 * that calls `onRetry`. Dismissing with "Dismiss" removes the toast. Multiple failures stack.
 *
 * Retry payloads live in memory; a failure the user ignores before closing the tab is
 * acceptable loss for optimistic flows.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2 } from 'lucide-react';
import { btnGhost, btnPrimary } from '../design/ui';
import { Icon } from '../design/Icon';
import { InfoTip } from './InfoTip';
import { errorDetail, errorText } from '../lib/inputErrors';

interface ToastEntry {
  id: number;
  message: string;
  /** Technical detail (error code, rejected fields) for the (i). */
  detail?: string | null;
  onRetry?: () => void;
}

interface StatusCard {
  id: number;
  label: string;
  state: 'pending' | 'saved';
}

interface FireOptions {
  /** Called when the user clicks Retry in the failure toast. */
  onRetry?: () => void;
  /** Pending-card text while the job runs (default "Saving…"). */
  label?: string;
  /** Card text once a visibly pending job succeeds (default "Saved"). */
  successLabel?: string;
  /** Show the success card even when the job finished before the pending card appeared. */
  confirm?: boolean;
}

type FireAndForget = (fn: () => Promise<unknown>, opts?: FireOptions) => void;

const Ctx = createContext<FireAndForget | null>(null);

/** A job must run this long before its card shows (no flicker for quick saves). */
const SHOW_AFTER_MS = 1200;
/** How long the "Saved" state stays on screen. */
const SAVED_MS = 2500;

let nextId = 1;

// Module-level so <AutoUpdate/> can hold off reloading while a submit is in
// flight or a failure toast (with its in-memory Retry) is still open.
let inFlight = 0;
let openToasts = 0;

/** True while a reload would lose a pending submit or an unanswered failure toast. */
export function hasUnfinishedBackgroundWork(): boolean {
  return inFlight > 0 || openToasts > 0;
}

function warnBeforeUnload(e: BeforeUnloadEvent) {
  e.preventDefault();
  e.returnValue = '';
}

export function BackgroundSubmitProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const [cards, setCards] = useState<StatusCard[]>([]);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    openToasts = toasts.length;
  }, [toasts]);

  useEffect(() => {
    const map = timers.current;
    return () => { map.forEach((t) => window.clearTimeout(t)); map.clear(); };
  }, []);

  const fireAndForget = useCallback<FireAndForget>((fn, opts) => {
    const jobId = nextId++;
    const label = opts?.label ?? 'Saving…';
    let shown = false;
    inFlight++;
    if (inFlight === 1) window.addEventListener('beforeunload', warnBeforeUnload);

    const showTimer = window.setTimeout(() => {
      shown = true;
      setCards((prev) => [...prev, { id: jobId, label, state: 'pending' }]);
    }, SHOW_AFTER_MS);

    const finish = () => {
      window.clearTimeout(showTimer);
      inFlight--;
      if (inFlight === 0) window.removeEventListener('beforeunload', warnBeforeUnload);
    };

    void Promise.resolve().then(fn).then(() => {
      finish();
      if (!shown && !opts?.confirm) return;
      const done: StatusCard = { id: jobId, label: opts?.successLabel ?? 'Saved', state: 'saved' };
      setCards((prev) => (shown ? prev.map((c) => (c.id === jobId ? done : c)) : [...prev, done]));
      const t = window.setTimeout(() => {
        timers.current.delete(jobId);
        setCards((prev) => prev.filter((c) => c.id !== jobId));
      }, SAVED_MS);
      timers.current.set(jobId, t);
    }).catch((err: unknown) => {
      finish();
      if (shown) setCards((prev) => prev.filter((c) => c.id !== jobId));
      const raw = errorText(err, 'Something went wrong. Please try again.');
      // Firebase callable errors often look like "FirebaseError: functions/... message"
      // Strip the leading "FirebaseError: functions/<code> " prefix for readability.
      const message = raw.replace(/^FirebaseError:\s*functions\/[^\s]+\s*/i, '').trim() || raw;
      const id = nextId++;
      setToasts((prev) => [
        ...prev,
        { id, message, detail: errorDetail(err), onRetry: opts?.onRetry },
      ]);
    });
  }, []);

  const pending = cards.filter((c) => c.state === 'pending');
  const saved = cards.filter((c) => c.state === 'saved');
  const cardClass = 'max-w-[340px] w-full rounded-xl border border-line bg-surface text-fg shadow-lg px-4 py-3 flex items-center gap-2 text-sm';

  return (
    <Ctx.Provider value={fireAndForget}>
      {children}
      {/* Status + toast stack — bottom-right, portalled so no ancestor can clip or offset it */}
      {createPortal(
        <div
          aria-live="polite"
          className="fixed bottom-24 md:bottom-4 right-4 z-toast flex flex-col gap-2 items-end"
        >
          {pending.length > 0 && (
            <div className={cardClass} role="status">
              <Icon as={Loader2} className="text-fg-muted motion-safe:animate-spin" />
              <span>{pending.length > 1 ? `${pending.length} items saving` : pending[0]?.label}</span>
            </div>
          )}
          {saved.map((c) => (
            <div key={c.id} className={cardClass} role="status">
              <Icon as={Check} className="text-success" />
              <span>{c.label}</span>
            </div>
          ))}
          {toasts.map((t) => (
            <div
              key={t.id}
              className="max-w-[340px] w-full rounded-xl border border-line bg-surface text-fg shadow-lg px-4 py-3"
            >
              <p className="text-sm text-danger font-medium">Submission failed</p>
              <p className="text-xs text-fg-muted mt-1 leading-relaxed break-words">
                {t.message}
                {t.detail && <InfoTip text={t.detail} label="the error code" align="end" className="ml-1.5" />}
              </p>
              <div className="mt-2 flex items-center gap-3">
                {t.onRetry && (
                  <button
                    type="button"
                    onClick={() => {
                      dismiss(t.id);
                      t.onRetry?.();
                    }}
                    className={btnPrimary}
                  >
                    Retry
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => dismiss(t.id)}
                  className={btnGhost}
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  );
}

/**
 * Returns a stable `fireAndForget` function.
 * Must be used inside <BackgroundSubmitProvider>.
 */
export function useFireAndForget(): FireAndForget {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useFireAndForget must be used inside <BackgroundSubmitProvider>');
  return ctx;
}
